import { Component } from '@theme/component';

/** @typedef {{ buttons: HTMLButtonElement[], panels: HTMLElement[] }} FaqTabsRefs */

/** @extends {Component<FaqTabsRefs>} */
export class FaqTabsComponent extends Component {
  requiredRefs = ['buttons', 'panels'];
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();

    this.#syncButtonsFromPanels();

    const { buttons } = this.refs;
    if (!buttons?.length) return;

    this.addEventListener('click', this.#handleClick, { signal: this.#abort.signal });
    this.addEventListener('keydown', this.#handleKeydownEvent, { signal: this.#abort.signal });

    this.#activate(this.dataset.initialFilter || buttons[0]?.dataset.filter || 'all');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncButtonsFromPanels();
    const activeButton = this.refs.buttons?.find((button) => button.getAttribute('aria-pressed') === 'true');
    this.#activate(activeButton?.dataset.filter || this.dataset.initialFilter || 'all');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * @param {string} filter
   */
  #activate(filter) {
    const { buttons, panels } = this.refs;
    if (!buttons || !panels) return;

    for (const button of buttons) {
      const active = (button.dataset.filter || 'all') === filter;
      button.setAttribute('aria-pressed', String(active));
      button.tabIndex = active ? 0 : -1;
    }

    for (const panel of panels) {
      const active = filter === 'all' || panel.dataset.faqCategory === filter;
      panel.hidden = !active;
    }
  }

  #syncButtonsFromPanels() {
    const buttonsContainer = this.refs.buttonsContainer;
    const panels = this.refs.panels;
    const showAllButton = this.refs.buttons?.find((button) => (button.dataset.filter || 'all') === 'all');

    if (!(buttonsContainer instanceof HTMLElement) || !panels?.length || !(showAllButton instanceof HTMLButtonElement)) return;

    buttonsContainer.querySelectorAll('[data-generated-faq-tab="true"]').forEach((button) => button.remove());

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;

      const title = panel.dataset.faqCategoryTitle?.trim();
      const filter = panel.dataset.faqCategory?.trim();
      if (!title || !filter) continue;

      const button = document.createElement('button');
      button.className = showAllButton.className;
      button.type = 'button';
      button.setAttribute('ref', 'buttons[]');
      button.dataset.filter = filter;
      button.dataset.generatedFaqTab = 'true';
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = `<span class="faq-template__tab-label"></span>`;

      const label = button.querySelector('.faq-template__tab-label');
      if (label) label.textContent = title;

      buttonsContainer.append(button);
    }

    this.refs.buttons = /** @type {HTMLButtonElement[]} */ (Array.from(buttonsContainer.querySelectorAll('[ref="buttons[]"]')));
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-filter]');
    if (!(button instanceof HTMLButtonElement)) return;
    this.#activate(button.dataset.filter || 'all');
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydownEvent = (event) => {
    const { buttons } = this.refs;
    if (!buttons?.length) return;

    const button = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-filter]');
    if (!(button instanceof HTMLButtonElement)) return;

    const index = buttons.indexOf(button);
    if (index < 0) return;

    const isNext = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const isPrev = event.key === 'ArrowLeft' || event.key === 'ArrowUp';

    let nextIndex = index;

    if (isNext) {
      nextIndex = (index + 1) % buttons.length;
    } else if (isPrev) {
      nextIndex = (index - 1 + buttons.length) % buttons.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = buttons.length - 1;
    } else {
      return;
    }

    event.preventDefault();

    const nextButton = buttons[nextIndex];
    if (!nextButton) return;

    nextButton.focus();
    this.#activate(nextButton.dataset.filter || 'all');
  };
}

if (!customElements.get('faq-tabs-component')) {
  customElements.define('faq-tabs-component', FaqTabsComponent);
}
